import React, {useEffect, useRef, useState} from "react";
import PropTypes from 'prop-types';

import {dataManager} from "../managers/DataManager";
import {flowManager} from "../managers/FlowManager";
import {userManager} from "../managers/UserManager";
import {StepHeader} from "../components/StepHeader";
import {StepCategory} from "../components/StepCategory";
import {TileCheckbox} from "../components/TileCheckbox";
import {SubCategoryTile} from "../components/SubCategoryTile";
import {dataKeys} from "../enums/dataKeys";

HomeyProMiniCategoryStep.propTypes = {
  index: PropTypes.number
}

export function HomeyProMiniCategoryStep(props) {
  const name = HomeyProMiniCategoryStep.name;
  const data = dataManager.store(state => state.data); // Data is updated on state change.
  const steps = flowManager.store(state => state.steps); // Steps is updated on state change.
  const stepRef = useRef(null);
  const updateAvailableRef = useRef(null);
  const isMountedRef = useRef(true);
  const [checkValue, setCheckValue] = useState("");
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const homey = userManager.getSelectedHomey();
  const options = {};

  // On mount, focus the step and scroll into view.
  useEffect(() => {
    stepRef.current.focus({ preventScroll: true });
    window.scrollTo({
      top: (stepRef.current.offsetTop - 120),
      behavior: "smooth"
    });
  }, [])

  useEffect(() => {
    const entry = data.find(item => item.name === name);
    if (entry) {
      setCheckValue(entry.value);
    }
  }, [data, name])

  useEffect(() => {
    if (!homey) return;
    isMountedRef.current = true;

    // On mount, check if the selected Homey Pro is up-to-date
    function getUpdateAvailable() {
      homey.authenticate()
        .then((homeyApi) => {
          return homeyApi.updates.getUpdates();
        })
        .then((updates) => {
          if (!isMountedRef.current) return
          if (Array.isArray(updates)) {
            setUpdateAvailable(updates.length > 0);
          } else {
            console.error("Updates is not an array: ", updates);
          }
        })
        .catch((error) => {
          console.error("Couldn't get updates:", error.message);
        });
    }

    if (!updateAvailableRef.current) {
      updateAvailableRef.current = true;
      getUpdateAvailable();
    }

    return () => {
      isMountedRef.current = false;
    }
  }, [homey, name, props.index])

  function handleSelect(event) {
    const value = event.target.value;
    const data = options[value].data;
    const action = data.action;
    const entry = { name, value, ...data };

    dataManager.updateData(props.index, entry); // Update the dataManager as well, otherwise first click will not mark subStep input as checked.
    flowManager.nextActionStep(props.index, entry, action);
  }

  // Add-ons
  options[`addOns-0`] = {
    title: "Homey Bridge",
    icon: "homey-bridge",
    data: { ...HomeyProMiniCategoryStep.homeyBridge }
  };
  options[`addOns-1`] = {
    title: "Homey Portal",
    icon: "homey-portal",
    data: { ...HomeyProMiniCategoryStep.homeyPortal }
  };
  options[`addOns-2`] = {
    title: "Homey Energy Dongle",
    icon: "energy-dongle",
    data: { ...HomeyProMiniCategoryStep.homeyEnergyDongle }
  };

  // Apps & Devices
  options[`appsAndDevices-0`] = {
    title: "Apps",
    icon: "apps",
    data: { ...HomeyProMiniCategoryStep.apps }
  };
  options[`appsAndDevices-1`] = {
    title: "Devices",
    icon: "devices",
    data: { ...HomeyProMiniCategoryStep.devices }
  };

  // Automation
  options[`automation-0`] = {
    title: "Flow",
    icon: "flow",
    data: { ...HomeyProMiniCategoryStep.flow }
  };
  options[`automation-1`] = {
    title: "Advanced Flow",
    icon: "advanced-flow",
    data: { ...HomeyProMiniCategoryStep.advancedFlow }
  };
  options[`automation-2`] = {
    title: "Logic",
    icon: "logic",
    data: { ...HomeyProMiniCategoryStep.logic }
  };

  // Monitoring
  options[`monitoring-0`] = {
    title: "Insights",
    icon: "insights",
    data: { ...HomeyProMiniCategoryStep.insights }
  };
  options[`monitoring-1`] = {
    title: "Energy",
    icon: "energy",
    data: { ...HomeyProMiniCategoryStep.energy }
  };
  options[`monitoring-2`] = {
    title: "Dashboards",
    icon: "dashboards",
    data: { ...HomeyProMiniCategoryStep.dashboards }
  };

  // Platform
  options['platform-0'] = {
    title: "Mobile App",
    icon: "phone",
    data: { ...HomeyProMiniCategoryStep.mobileApp }
  };
  options['platform-1'] = {
    title: "Web App",
    icon: "webapp",
    data: { ...HomeyProMiniCategoryStep.webApp }
  };

  // Voice Assistants
  options[`voiceAssistants-0`] = {
    title: "Amazon Alexa",
    icon: "alexa",
    data: { ...HomeyProMiniCategoryStep.alexa }
  };
  options[`voiceAssistants-1`] = {
    title: "Google Assistant",
    icon: "google",
    data: { ...HomeyProMiniCategoryStep.google }
  };
  options[`voiceAssistants-2`] = {
    title: "Siri",
    icon: "siri",
    data: { ...HomeyProMiniCategoryStep.siri }
  };

  // Warranty
  options[`warranty-0`] = {
    title: "Defects",
    icon: "defects",
    data: { ...HomeyProMiniCategoryStep.defects }
  };
  options[`warranty-1`] = {
    title: "Returns",
    icon: "returns",
    data: { ...HomeyProMiniCategoryStep.returns }
  };

  // Wireless Technologies
  options[`wireless-0`] = {
    title: "Matter",
    icon: "matter",
    data: { ...HomeyProMiniCategoryStep.matter }
  };
  options[`wireless-1`] = {
    title: "Thread",
    icon: "thread",
    data: { ...HomeyProMiniCategoryStep.thread }
  };
  options[`wireless-2`] = {
    title: "Zigbee",
    icon: "zigbee",
    data: { ...HomeyProMiniCategoryStep.zigbee }
  };

  // More
  options[`more-0`] = {
    title: "Family",
    icon: "family",
    data: { ...HomeyProMiniCategoryStep.family }
  };
  options[`more-3`] = {
    title: "Backups",
    icon: "backups",
    data: { ...HomeyProMiniCategoryStep.backups }
  };
  options[`more-4`] = {
    title: "Other",
    icon: "other",
    data: { ...HomeyProMiniCategoryStep.other }
  };

  return (
    <>
      <div ref={stepRef} tabIndex={0} className="support-form-step">
        <StepHeader
          index={props.index}
          question="How can we help you with Homey Pro mini?"
          description="Choose the category that describes your question or problem best."
        >
          <div className={`support-form__warning ${updateAvailable && "is-visible"}`}>
            <div className="support-form-icon"/>
            <div>
              <p className="support-form-title">Homey Pro mini’s software is out of date.</p>
              <p className="support-form-text">
                Updating Homey Pro mini to the latest firmware version may resolve your issue.
              </p>
            </div>
          </div>
        </StepHeader>
        <div className="support-form-step__categories">
          <StepCategory heading="Add-ons">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("addOns") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Apps & Devices">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("appsAndDevices") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Automation">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("automation") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Monitoring">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("monitoring") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Platform">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("platform") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Voice Assistants">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("voiceAssistants") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Warranty">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("warranty") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="Wireless Technologies">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("wireless") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
          <StepCategory heading="More">
            {Object.entries(options).map(([value, option]) => {
              return value.startsWith("more") && (
                <TileCheckbox key={value} name={name} value={value} check={checkValue} onChange={handleSelect}>
                  <SubCategoryTile title={option.title} icon={option.icon}/>
                </TileCheckbox>
              )
            })}
          </StepCategory>
        </div>
      </div>
      {steps.length === props.index + 1 && (
        <div className="support-form-step">
          <StepHeader
            index={props.index + 1}
            question="Select one of the above options."
          />
        </div>
      )}
    </>
  );
}

HomeyProMiniCategoryStep.homeyBridge = {
  action: "homeyBridge",
  [dataKeys.labels]: ["add-ons", "homey-bridge"],
  [dataKeys.path]: ["Add-ons", "Homey Bridge"]
}
HomeyProMiniCategoryStep.homeyPortal = {
  action: "homeyPortal",
  [dataKeys.labels]: ["add-ons", "homey-portal"],
  [dataKeys.path]: ["Add-ons", "Homey Portal"]
}
HomeyProMiniCategoryStep.homeyEnergyDongle = {
  action: "homeyEnergyDongle",
  [dataKeys.labels]: ["add-ons", "homey-energy-dongle"],
  [dataKeys.path]: ["Add-ons", "Homey Energy Dongle"]
}
HomeyProMiniCategoryStep.apps = {
  action: "apps",
  [dataKeys.labels]: ["apps"],
  [dataKeys.path]: ["Apps"]
}
HomeyProMiniCategoryStep.devices = {
  action: "devices",
  [dataKeys.labels]: ["devices"],
  [dataKeys.path]: ["Devices"]
}
HomeyProMiniCategoryStep.flow = {
  action: "flow",
  [dataKeys.labels]: ["flow"],
  [dataKeys.path]: ["Flow"]
}
HomeyProMiniCategoryStep.advancedFlow = {
  action: "advancedFlow",
  [dataKeys.labels]: ["advanced-flow"],
  [dataKeys.path]: ["Advanced Flow"]
}
HomeyProMiniCategoryStep.logic = {
  action: "logic",
  [dataKeys.labels]: ["logic"],
  [dataKeys.path]: ["Logic"]
}
HomeyProMiniCategoryStep.insights = {
  action: "insights",
  [dataKeys.labels]: ["insights"],
  [dataKeys.path]: ["Insights"]
}
HomeyProMiniCategoryStep.energy = {
  action: "energy",
  [dataKeys.labels]: ["energy"],
  [dataKeys.path]: ["Energy"]
}
HomeyProMiniCategoryStep.dashboards = {
  action: "dashboards",
  [dataKeys.labels]: ["dashboards"],
  [dataKeys.path]: ["Dashboards"]
}
HomeyProMiniCategoryStep.mobileApp = {
  action: "mobileApp",
  [dataKeys.labels]: ["mobile-app"],
  [dataKeys.path]: ["Mobile App"]
}
HomeyProMiniCategoryStep.webApp = {
  action: "webApp",
  [dataKeys.labels]: ["web-app"],
  [dataKeys.path]: ["Web App"]
}
HomeyProMiniCategoryStep.alexa = {
  action: "alexa",
  [dataKeys.labels]: ["alexa"],
  [dataKeys.path]: ["Amazon Alexa"]
}
HomeyProMiniCategoryStep.google = {
  action: "google",
  [dataKeys.labels]: ["google-assistant"],
  [dataKeys.path]: ["Google Assistant"]
}
HomeyProMiniCategoryStep.siri = {
  action: "siri",
  [dataKeys.labels]: ["siri"],
  [dataKeys.path]: ["Siri"]
}
HomeyProMiniCategoryStep.defects = {
  action: "defects",
  [dataKeys.labels]: ["defects"],
  [dataKeys.path]: ["Defects"]
}
HomeyProMiniCategoryStep.returns = {
  action: "returns",
  [dataKeys.labels]: ["returns"],
  [dataKeys.path]: ["Returns"]
}
HomeyProMiniCategoryStep.matter = {
  action: "matter",
  [dataKeys.labels]: ["matter"],
  [dataKeys.path]: ["Matter"]
}
HomeyProMiniCategoryStep.thread = {
  action: "thread",
  [dataKeys.labels]: ["thread"],
  [dataKeys.path]: ["Thread"]
}
HomeyProMiniCategoryStep.zigbee = {
  action: "zigbee",
  [dataKeys.labels]: ["zigbee"],
  [dataKeys.path]: ["Zigbee"]
}
HomeyProMiniCategoryStep.family = {
  action: "family",
  [dataKeys.labels]: ["family"],
  [dataKeys.path]: ["Family"]
}
HomeyProMiniCategoryStep.backups = {
  action: "backups",
  [dataKeys.labels]: ["backups"],
  [dataKeys.path]: ["Backups"]
}
HomeyProMiniCategoryStep.other = {
  action: "other",
  [dataKeys.labels]: ["other"],
  [dataKeys.path]: ["Other"]
}

